import React, { useState, useEffect } from 'react';
// Correct imports for tanstack/react-router v1
import { useSearch, useNavigate, Link } from '@tanstack/react-router'; 
import { postAnon } from '../api'; // Correct import
// Import custom components
import { TextInput } from '../components/TextInput';
import { Button } from '../components/Button';
import { useSEO } from '../hooks/useSEO';

// Removed useQuery hook as useSearch is preferred in tanstack/react-router v1

// Copy password requirements from SignUp.js
const passwordRequirements = [
  { test: (p) => p.length >= 8, text: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), text: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p), text: 'One lowercase letter' },
  { test: (p) => /[0-9]/.test(p), text: 'One number' }
];

function ResetPassword() {
  useSEO({ title: 'Reset password – Compress GLB', robots: 'noindex, follow' });

    // Use useSearch hook for query parameters
    const { token: tokenFromQuery, user: usernameFromQuery } = useSearch({ 
      // Specify expected search params (optional but good practice)
      from: '/reset-password',
      select: (search) => ({
        token: search.token,
        user: search.user,
      }),
     }); 
    const navigate = useNavigate();
    const [token, setToken] = useState('');
    const [username, setUsername] = useState(''); 
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Get params directly from useSearch hook results
        if (tokenFromQuery && usernameFromQuery) {
            setToken(tokenFromQuery);
            setUsername(usernameFromQuery);
        } else {
            setError('Invalid or missing reset link parameters.');
        }
    }, [tokenFromQuery, usernameFromQuery]); // Dependencies updated

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        
        // Use the imported requirements for validation logic
        const passwordError = passwordRequirements
            .map(req => !req.test(password) ? req.text : null)
            .filter(Boolean)[0]; // Get the first failing requirement
            
        if (passwordError) {
            // Map specific requirement text to a user-friendly error
            // This replicates the logic in SignUp.js getPasswordError more closely
            let errorMessage = `Password must contain ${passwordError.toLowerCase()}.`;
            if (passwordError.includes('characters')) {
                errorMessage = 'Password must be at least 8 characters.';
            } 
            setError(errorMessage);
            return;
        }

        setLoading(true);

        try {
            const response = await postAnon('/api/1/auth/reset-password', { 
                token,
                username,
                new_password: password
            });

            setMessage('Password reset successful! Signing you in...');
            
            if (response && response.access_token && response.refresh_token) {
                // Use localStorage directly, like in SignIn.js
                localStorage.setItem('access_token', response.access_token);
                localStorage.setItem('refresh_token', response.refresh_token);
                
                setTimeout(() => {
                    navigate({ to: '/app/dashboard' }); 
                }, 1500); 
            } else {
                setError('Sign-in after reset failed. Please try signing in manually.');
                setTimeout(() => {
                    navigate({ to: '/sign-in' }); 
                }, 3000);
            }

        } catch (err) {
            console.error("Reset password error:", err);
            const apiErrorMessage = err?.data?.message || err?.message || 'Failed to reset password. The link may be invalid or expired.';
            setError(apiErrorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (!token || !username) {
        // Render message if token/username is missing or invalid
        return (
            <div className="bg-background w-screen min-h-screen flex justify-center items-center px-4 py-10">
                <div className="bg-background-elevated p-8 md:p-10 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 w-full max-w-md space-y-6 border border-border/60 text-center">
                    <h2 className="text-2xl font-semibold text-danger">Error</h2>
                    <p className="text-muted-foreground">{error || 'Invalid password reset link.'}</p>
                    <Link to="/sign-in" className="inline-block text-accent hover:text-accent/90 font-medium transition-colors">
                        Go to Sign In
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-background w-screen min-h-screen flex justify-center items-center px-4 py-10">
            <div className="bg-background-elevated p-8 md:p-10 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 w-full max-w-md space-y-6 border border-border/60">
                <header className="space-y-2 text-center">
                    <h2 className="text-2xl font-semibold text-foreground tracking-tight">Reset Password</h2>
                    <p className="text-sm text-muted-foreground">Choose a new password for your account</p>
                </header>
                
                {/* Display success/error messages with theme styling */}
                {message && (
                  <div 
                    role="status" 
                    className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded-lg text-sm"
                  >
                    {message}
                  </div>
                )}
                {error && (
                    <div 
                        role="alert" 
                        className="bg-danger/10 border border-danger/20 text-danger px-4 py-3 rounded-lg text-sm"
                    >
                        {error}
                    </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                    {/* Wrap Input and checklist in a div for spacing */}
                    <div className="space-y-3">
                        <TextInput
                            label="New Password"
                            id="password"
                            name="password"
                            type="password"
                            required
                            placeholder="Enter new password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value)
                                if(error) setError('') // Clear error on change
                            }}
                            disabled={loading}
                            showPasswordToggle
                        />
                        {/* Add the requirements checklist */}
                        <div className="space-y-2 text-sm bg-background-soft/50 rounded-lg p-3 border border-border/40">
                            {passwordRequirements.map(({ test, text }, i) => (
                                <div key={i} className="flex items-center gap-2.5 text-muted-foreground">
                                <span className={`text-sm font-medium ${test(password) ? 'text-success' : 'text-muted-foreground/60'}`}>
                                    {test(password) ? '✓' : '○'}
                                </span>
                                <span className={test(password) ? 'text-foreground' : ''}>{text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <TextInput
                        label="Confirm New Password"
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        required
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.target.value)
                             if(error) setError('') // Clear error on change
                        }}
                        disabled={loading}
                        showPasswordToggle
                        error={error && error.includes('match') ? error : ''}
                    />

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full"
                        size="lg"
                    >
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                </form>
            </div>
        </div>
    );
}

export default ResetPassword; 